import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { useLang } from '../../i18n';
import { useSeasons, useSeason } from '../../lib/useSeasons';
import { Reveal } from '../Reveal';
import { ProductTile } from '../ProductTile';
import { Img } from '../Img';
import { seasonMedia, altOf } from '../../lib/seasonMedia';

/**
 * La stagione in home.
 *
 * Non e' una sezione «Natale»: e' la sezione della stagione in corso, e il
 * contenuto — nome, claim, pezzi — arriva dal registro. Quando cambia
 * la stagione cambia questo blocco, senza toccare la home.
 *
 * Anticipa la pagina invece di raccontarla due volte: le idee e le regole
 * stanno solo di la'.
 */
export const SeasonSection = () => {
  const { t, lang } = useLang();
  const { voce } = useSeasons();
  const { data: s, visible } = useSeason(voce?.slug);

  if (!s || !visible) return null;
  const pezzi = (s.products || []).slice(0, 3);
  const foto = seasonMedia(s.slug)?.home;

  const testo = (
    <div>
      {/* Il titolo e' uno: niente kicker sopra e niente parola in colore dentro. */}
      <h2 className="text-[30px] leading-[1.06] md:text-[40px] lg:text-[52px]">{s.claim}</h2>

      <p className="prosa mt-7 max-w-[50ch]">{s.lead}</p>

      <div className="mt-9 flex flex-wrap items-center gap-x-7 gap-y-4">
        <Link to={`/stagioni/${s.slug}`} data-testid="home-season-cta" className="btn btn-ink">
          {s.enter}
          <ArrowUpRight size={15} strokeWidth={2} />
        </Link>
        <span className="text-[14px] text-inchiostro/70">
          {s.pieces_title} · {s.product_count}
        </span>
        {!s.active && (
          <span data-testid="home-season-preview-badge" className="bg-inchiostro/10 px-2 py-1 text-[14px]">
            {t('season.preview')}
          </span>
        )}
      </div>
    </div>
  );

  // tre pezzi veri della stagione, non un'illustrazione
  const pezziLista = (
    <ul className={`grid grid-cols-3 gap-4 md:gap-6 ${foto ? 'mt-10' : ''}`}>
      {pezzi.map((p, i) => (
        <Reveal as="li" key={p.sku} delay={i * 0.08}>
          <ProductTile product={p} />
        </Reveal>
      ))}
    </ul>
  );

  return (
    <section
      data-testid="home-season"
      className="bg-carta-2 py-14 md:py-20"
    >
      <div className="wrap">
        {foto ? (
          <div className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:items-center lg:gap-20">
            <figure className="relative aspect-[4/5] overflow-hidden bg-carta lg:aspect-[3/4]">
              <Img
                src={foto.src}
                alt={altOf(foto, lang)}
                width={1200}
                aspectRatio={3 / 4}
                sizes="(min-width: 1024px) 40vw, 100vw"
                data-testid="home-season-photo"
                className="img-zoom h-full w-full object-cover"
              />
            </figure>
            <div>
              {testo}
              {pezziLista}
            </div>
          </div>
        ) : (
          <div className="grid gap-10 lg:grid-cols-[1fr_1.05fr] lg:items-center lg:gap-20">
            {testo}
            {pezziLista}
          </div>
        )}
      </div>
    </section>
  );
};
